/**
 * Analysis types as they appear on the AMQP messages exchanged with tracing-ai.
 * These are the canonical chunk `type` values stored on
 * `tracing_ai_summary_transactions` / `tracing_ai_link_results`.
 */
export const TRACING_AI_ANALYSIS_TYPES = {
  CLASSIFY: 'classify',
  ANALYZE: 'analyze',
  VALIDATE: 'validate',
} as const;

export type TracingAiAnalysisType =
  (typeof TRACING_AI_ANALYSIS_TYPES)[keyof typeof TRACING_AI_ANALYSIS_TYPES];

/** Per-chunk lifecycle status on `tracing_ai_summary_transactions`. */
export const TRACING_AI_CHUNK_STATUSES = {
  PROCESSING: 'PROCESSING',
  PROCESSED: 'PROCESSED',
  FAILED: 'FAILED',
} as const;

export type TracingAiChunkStatus =
  (typeof TRACING_AI_CHUNK_STATUSES)[keyof typeof TRACING_AI_CHUNK_STATUSES];

/** Per-type status stored inside the `tracing_ai_summaries` JSONB columns. */
export const TRACING_AI_SUMMARY_STATUSES = {
  PREPARING: 'PREPARING',
  PROCESSING: 'PROCESSING',
  PROCESSED: 'PROCESSED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;

export type TracingAiSummaryStatus =
  (typeof TRACING_AI_SUMMARY_STATUSES)[keyof typeof TRACING_AI_SUMMARY_STATUSES];

/** A per-type status is terminal once it can no longer transition. */
export const TRACING_AI_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  TRACING_AI_SUMMARY_STATUSES.PROCESSED,
  TRACING_AI_SUMMARY_STATUSES.FAILED,
  TRACING_AI_SUMMARY_STATUSES.CANCELLED,
]);

/** Maps an AMQP analysis `type` to its `tracing_ai_summaries` JSONB column. */
export const ANALYSIS_TYPE_TO_COLUMN: Record<TracingAiAnalysisType, string> = {
  [TRACING_AI_ANALYSIS_TYPES.CLASSIFY]: 'doc_type',
  [TRACING_AI_ANALYSIS_TYPES.ANALYZE]: 'doc_content',
  [TRACING_AI_ANALYSIS_TYPES.VALIDATE]: 'sc_analysis',
};

/** Human-readable label per analysis type (used in credit-transaction descriptions). */
export const ANALYSIS_TYPE_LABELS: Record<TracingAiAnalysisType, string> = {
  [TRACING_AI_ANALYSIS_TYPES.CLASSIFY]: 'Document Classification',
  [TRACING_AI_ANALYSIS_TYPES.ANALYZE]: 'Document Analysis',
  [TRACING_AI_ANALYSIS_TYPES.VALIDATE]: 'Supply Chain Analysis',
};

/** The two order kinds a tracing summary can describe. */
export const TRACING_TYPES = {
  PURCHASE_ORDER: 'PURCHASE_ORDER',
  SALES_ORDER: 'SALES_ORDER',
} as const;

export type TracingType = (typeof TRACING_TYPES)[keyof typeof TRACING_TYPES];

/** Order table backing each tracing type. */
export const ORDER_TABLE_BY_TRACING_TYPE: Record<TracingType, string> = {
  [TRACING_TYPES.PURCHASE_ORDER]: 'tracing_purchase_orders',
  [TRACING_TYPES.SALES_ORDER]: 'tracing_sales_orders',
};

/** Short order label per tracing type (e.g. "PO #123"). */
export const ORDER_LABEL_BY_TRACING_TYPE: Record<TracingType, string> = {
  [TRACING_TYPES.PURCHASE_ORDER]: 'PO',
  [TRACING_TYPES.SALES_ORDER]: 'SO',
};
